import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import NaturePeopleIcon from '@mui/icons-material/NaturePeople';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tooltip,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { DatePicker } from 'antd';
import classNames from 'classnames';
import {
  addDays,
  differenceInDays,
  endOfDay,
  endOfMonth,
  format,
  getUnixTime,
  isFuture,
  isToday,
  startOfDay,
  startOfMonth,
  subDays,
} from 'date-fns';
import dayjs from 'dayjs';
import * as React from 'react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { usePersistedDateRange } from '@/hooks/usePersistedDateRange';
import useSWR from 'swr';

import { IInvoices, ILineItems } from '@/Billing/types';
import { useBills } from '@/Billing/useBills';
import { DeviceStatusTable } from '@/components/DeviceStatusTable';
import { DeleteConfirmDialog } from '@/components/Dialog';
import { SWLoadingButton } from '@/components/LoadingButton';
import { SWLoadingButtonOutlined } from '@/components/SWLoadingButtonOutlined';
import { defaultOutageStatusResponse, getOutageStatus } from '@/Outage/api';
import {
  mapOutageStatusToIsElasticsearchOutage,
  OUTAGE_STATUS_REFRESH_MILLIS,
  OUTAGE_STATUS_SWR_KEY,
} from '@/Outage/util';
import { getUserInfo } from '@/pages/AccountSettings/api';
import { AlertsView } from '@/Structures/alerts';
import { convertTimestampToTimeZone } from '@/util';

import { createStructureRoute } from '../../app-routes';
import { Chart } from '../../components/Chart';
import { createLabel as createChartLabel } from '../../components/Chart/util';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { useStructure } from '../../Structures/useStructures';
import theme from '../../theme';
import type { Address as AddressType } from '../../types';
import { UsageGraphMode } from '../types';
import { get as getUsage } from '../../Usage/api';
import { getTotalVolume } from '../../Usage/util';
import { addCreate, formatNumber } from '../../util';
import { edit, remove } from '../api';
import { BillHistoryTable } from '../billHistory';
import { useUnit } from '../useUnit';
import { generateUsageCSV } from '../util';
import { Address } from './Address';
import { Name } from './Name';
const { RangePicker } = DatePicker;

const dateFormat = 'YYYY-MM-DD';

// Component //////////////////////////////////////////////////////////////////

const useStyles = makeStyles((theme) => ({
  wrapper: {
    display: 'flex',
    width: '100%',
  },
  container: {
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
  },
  header: {
    alignItems: 'baseline',
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(3),
    paddingBottom: 0,
    paddingLeft: 0,
    textAlign: 'left',
  },
  chartBody: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
  },
  chartLoader: {
    left: '20%',
    position: 'absolute',
    top: '50%',
    width: '60%',
  },
  editIcon: {
    marginLeft: theme.spacing(1),
    marginTop: theme.spacing(-0.5),
    opacity: 0.3,
    '&:hover': {
      opacity: 1,
    },
  },
  prevNextButton: {
    height: 'fit-content',
  },
  nextButton: {
    marginLeft: theme.spacing(1),
  },
  prevButton: {
    marginRight: theme.spacing(1),
  },
}));

const today = new Date();
export interface IUnitBill {
  invoices: IInvoices[];
  cost: number;
  costUnits: 'DOLLARS';
  endDate: number;
  id: string;
  startDate: number;
  structureId: string;
  volume: number;
  volumeUnits: 'GALLONS';
  name?: string;
  rateMethod?: 'SIMPLESUB' | 'UTILITY' | 'CUSTOM';
  effectiveRate?: number | null;
  billsSentTimestamp?: number | null;
  lineItems: ILineItems[];
}

export const Unit = (): JSX.Element => {
  const [value, setValue] = useState('2');
  const navigate = useNavigate();
  const classes = useStyles();

  const [loading, setLoading] = React.useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = React.useState(false);
  const [graphData, setGraphData] = React.useState([
    { label: '', gallons: 0, volume: 0, date: format(today, 'yyyy-MM-dd') },
  ]);
  const { range, setRange, moveByMonth } = usePersistedDateRange();
  const [canGoNext, setCanGoNext] = React.useState(false);
  const [timeZone, setTimeZone] = React.useState<string>('');
  const [eventData] = React.useState<{}>();
  const [invoices, setInvoices] = React.useState<IUnitBill[]>();
  const [dialogModel, setDialogModel] = React.useState(false);
  const [unitModel, setUnitModel] = React.useState<
    'NON_BILLABLE' | 'COMMON_AREA' | 'SPLIT' | 'By_Usage'
  >();
  const [selectedModel, setSelectedModel] = React.useState<
    'NON_BILLABLE' | 'COMMON_AREA' | 'SPLIT' | 'By_Usage'
  >();
  const [stripeConnect, setStripeConnect] = React.useState<boolean | undefined>(undefined);

  React.useEffect(() => {
    const getData = async () => {
      const userInfo = await getUserInfo();
      if (userInfo) {
        const stripeInfo =
          userInfo.user.stripe_connected_account_id !== '' &&
          userInfo.user.stripe_connected_account_details_submitted
            ? true
            : false;
        setStripeConnect(stripeInfo);
      }
    };
    if (stripeConnect === undefined) getData();
  }, [stripeConnect]);

  const { structureId, unitId } = useParams();

  if (!unitId || !structureId) {
    throw new Error('MISSING_REQUIRED_ROUTE_DATA');
  }

  /** Structure */
  const { structure } = useStructure(structureId);

  const { bills = [], isLoading } = useBills(structureId);

  React.useEffect(() => {
    const getInvoices = () => {
      const filteredBills = bills.map((bill) => ({
        ...bill, // Copiamos todas las propiedades del bill original
        invoices: bill.invoices.filter((invoice) => invoice.unitId === unitId), // Filtramos los invoices
      }));
      setInvoices(filteredBills);
    };
    if (!isLoading && bills) getInvoices();
  }, [bills, isLoading, unitId]);

  React.useEffect(() => {
    if (structure && timeZone === '') {
      if (structure.timezone_key !== '') setTimeZone(structure.timezone_key);
      if (structure.timezone_key === '') {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        setTimeZone(tz);
      }
    }
  }, [structure, timeZone]);

  /** Unit, call to refresh unit data */
  const { unit, mutate: refreshData } = useUnit(unitId);

  const { data: outageStatusResponse = defaultOutageStatusResponse } = useSWR(
    OUTAGE_STATUS_SWR_KEY,
    getOutageStatus,
    {
      refreshInterval: OUTAGE_STATUS_REFRESH_MILLIS,
    },
  );

  // Load usage
  const { data: rawUsage, error: usageError } = useSWR(
    `units/${unitId}/usage?start=${getUnixTime(startOfDay(range[0]!))}&end=${getUnixTime(
      endOfDay(range[1]!),
    )}`,
    () =>
      getUsage(
        unitId,
        range[0]!,
        range[1]!,
        mapOutageStatusToIsElasticsearchOutage(outageStatusResponse),
        timeZone,
      ),
    {
      shouldRetryOnError: false, // Don't retry on permission errors
    },
  );

  // Check if error is permission denied
  const isPermissionDenied = usageError?.message?.includes('PERMISSION_DENIED');

  // Set graph data when rawUsage changes
  React.useEffect(() => {
    const usageByDate: { [dateStr: string]: number } = {};

    rawUsage?.devices?.forEach((device) => {
      device.daily_usages.forEach((usageData) => {
        addCreate(usageByDate, usageData.date, usageData.volume);
      });
    });

    const startStr = format(range[0]!, 'yyyy-MM-dd');
    const endStr = format(range[1]!, 'yyyy-MM-dd');

    const cleanedUsage = Object.entries(usageByDate).map(([date, vol]) => ({
      label: createChartLabel(vol as number, date),
      gallons: vol as number,
      volume: vol as number,
      date, // 'YYYY-MM-DD'
    }));

    const bounded = cleanedUsage
      .filter((d) => d.date >= startStr && d.date <= endStr)
      .sort((a, b) => (a.date < b.date ? -1 : 1));

    // @ts-ignore
    if (bounded.length) {
      // @ts-ignore
      setGraphData(bounded);
    } else {
      // @ts-ignore
      setGraphData([{ label: '', gallons: 0, volume: 0, date: startStr }]);
    }
  }, [rawUsage, range]);

  // En/disable next button
  React.useEffect(() => {
    if (range[1]) {
      if (isFuture(range[1]) || isToday(range[1])) {
        setCanGoNext(false);
      } else {
        setCanGoNext(true);
      }
    }
  }, [range]);

  React.useEffect(() => {
    if (unit) {
      if (unit.unit_flags.length > 0) {
        const model: string[] = unit.unit_flags as string[];
        const index = model.indexOf('COMMON_AREA');
        if (index > -1) {
          // only splice array when item is found
          model.splice(index, 1); // 2nd parameter means remove one item only
        }
        setUnitModel(model[0] as 'NON_BILLABLE' | 'COMMON_AREA' | 'SPLIT' | 'By_Usage');
      }
      if (unit.unit_flags.length === 0) setUnitModel('By_Usage');
    }
  }, [unitModel, unit]);

  const handleClickNext = () => {
    if (moveByMonth) {
      const newMonth = addDays(range[1]!, 3);
      setRange([startOfDay(startOfMonth(newMonth)), startOfDay(endOfMonth(newMonth))]);
    } else {
      const numberOfDaysInRange = differenceInDays(range[1]!, range[0]!);

      const newFrom = addDays(range[0]!, numberOfDaysInRange + 1);
      const newTo = addDays(range[1]!, numberOfDaysInRange + 1);

      setRange([startOfDay(newFrom), startOfDay(newTo)]);
    }
  };

  const handleClickPrev = () => {
    if (moveByMonth) {
      const newMonth = subDays(range[0]!, 3);
      setRange([startOfDay(startOfMonth(newMonth)), startOfDay(endOfMonth(newMonth))]);
    } else {
      const numberOfDaysInRange = differenceInDays(range[1]!, range[0]!);

      const newFrom = subDays(range[0]!, numberOfDaysInRange + 1);
      const newTo = subDays(range[1]!, numberOfDaysInRange + 1);
      setRange([startOfDay(newFrom), startOfDay(newTo)]);
    }
  };

  const handleDateChange = (dates) => {
    const startDate = new Date(dates[0]);
    const endDate = new Date(dates[1]);
    startDate?.setHours(0, 0, 0, 0);
    endDate?.setHours(23, 59, 59, 0);
    setRange([startDate, endDate]);
  };

  const handleClickDeleteUnit = () => {
    setOpenDeleteConfirm(true);
  };

  const handleClickRemove = async () => {
    if (!unit) return;

    setLoading(true);
    try {
      await remove(unit.id);
      navigate(createStructureRoute(structureId));
      refreshData();
      setOpenDeleteConfirm(false);
    } catch (err) {
      // Error already shown by API layer, keep dialog open
      console.error('Failed to delete unit:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUnit = (fieldName: string) => async (value: string | AddressType) => {
    if (!unit) return;
    setLoading(true);
    try {
      await edit({ ...unit, [fieldName]: value });
      refreshData();
    } finally {
      setLoading(false);
    }
  };

  const handleToggleIncludeUsageGraph = async (
    _e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    if (!unit) return;
    setLoading(true);
    try {
      await edit({
        ...unit,
        usage_graph_mode: checked ? UsageGraphMode.INCLUDED : UsageGraphMode.HIDDEN,
      });
      refreshData();
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUnitFlags = async () => {
    if (!unit) return;
    setLoading(true);
    let flags: any[] = [];
    if (selectedModel !== 'By_Usage') flags = ['COMMON_AREA', selectedModel!];
    await edit({ ...unit, unit_flags: flags });
    refreshData();
    setDialogModel(false);
    setLoading(false);
  };

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  const isLoadingUsage = !graphData;
  return (
    <ErrorBoundary>
      <div data-component-id={'UNIT-VIEW'} className={classes.wrapper}>
        {unit && unitModel && (
          <div className={classes.container}>
            <div className={classes.header}>
              <div style={{ flexGrow: 1 }}>
                <Stack direction={'row'} spacing={0} justifyContent={'space-between'}>
                  <Box>
                    <Name
                      name={unit?.name || ''}
                      onUpdate={handleUpdateUnit('name')}
                      onDelete={handleClickDeleteUnit}
                    />

                    <Stack direction={'row'} spacing={0} alignItems={'center'}>
                      {unit.unit_flags.length > 0 ? (
                        <Stack direction={'row'} gap={1}>
                          <NaturePeopleIcon htmlColor="black" />
                          <Typography fontWeight={'bold'}>Common Area</Typography>
                          <Divider
                            orientation="vertical"
                            flexItem
                            sx={{ marginLeft: 1, borderWidth: 1 }}
                          />
                        </Stack>
                      ) : (
                        <Stack direction={'row'} gap={1}>
                          <MeetingRoomIcon htmlColor="black" />
                          <Typography fontWeight={'bold'}>Unit</Typography>
                          <Divider
                            orientation="vertical"
                            flexItem
                            sx={{ marginLeft: 1, borderWidth: 1 }}
                          />
                        </Stack>
                      )}

                      <Box marginLeft={1}>
                        <Address
                          address={
                            unit?.address || {
                              street: '',
                              street2: '',
                              city: '',
                              state: '',
                              zip: '',
                            }
                          }
                          onUpdate={handleUpdateUnit('address')}
                          loading={loading}
                        />
                      </Box>
                    </Stack>
                  </Box>
                  <Stack direction="row" spacing={3} alignItems="flex-start">
                    <Stack>
                      <Tooltip title="When enabled, generated custom invoice PDFs for this unit include the daily usage bar chart. Turn off if usage data is incomplete or estimated.">
                        <Typography fontWeight={'bold'} fontSize={15}>
                          Usage Graph on Invoices
                        </Typography>
                      </Tooltip>
                      <FormControlLabel
                        sx={{ mt: 0.5 }}
                        control={
                          <Switch
                            checked={unit.usage_graph_mode !== UsageGraphMode.HIDDEN}
                            onChange={handleToggleIncludeUsageGraph}
                            disabled={loading}
                            size="small"
                          />
                        }
                        label={
                          <Typography fontSize={13}>
                            {unit.usage_graph_mode !== UsageGraphMode.HIDDEN
                              ? 'Included'
                              : 'Hidden'}
                          </Typography>
                        }
                      />
                    </Stack>
                    <Stack>
                      <Typography fontWeight={'bold'} fontSize={15}>
                        Bill Model
                      </Typography>
                      <Select
                        labelId="demo-simple-select-standard-label"
                        id="demo-simple-select-standard"
                        value={unitModel}
                        onChange={(event) => {
                          setDialogModel(true);
                          setSelectedModel(
                            event.target.value as
                              | 'NON_BILLABLE'
                              | 'COMMON_AREA'
                              | 'SPLIT'
                              | 'By_Usage',
                          );
                        }}
                        variant="standard"
                      >
                        {unitModel === 'By_Usage' ? (
                          <MenuItem value={'By_Usage'}>By Usage</MenuItem>
                        ) : (
                          <MenuItem value={'By_Usage'} disabled>
                            By Usage
                          </MenuItem>
                        )}
                        {unitModel !== 'By_Usage' ? (
                          <MenuItem value={'SPLIT'}>Split</MenuItem>
                        ) : (
                          <MenuItem value={'SPLIT'} disabled>
                            Split
                          </MenuItem>
                        )}
                        {unitModel !== 'By_Usage' ? (
                          <MenuItem value={'NON_BILLABLE'}>Non Billable</MenuItem>
                        ) : (
                          <MenuItem value={'NON_BILLABLE'} disabled>
                            Non Billable
                          </MenuItem>
                        )}
                      </Select>
                    </Stack>
                  </Stack>
                </Stack>
                <TabContext value={value}>
                  <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <TabList onChange={handleChange} aria-label="lab API tabs example">
                      <Tab label="Usage" value="2" />
                      {stripeConnect && <Tab label="Billing" value="1" />}
                    </TabList>
                  </Box>
                  {stripeConnect && (
                    <TabPanel value="1">
                      {invoices && <BillHistoryTable data={invoices} timeZone={timeZone} />}
                    </TabPanel>
                  )}
                  <TabPanel value="2">
                    {structure && (
                      <DeviceStatusTable
                        devices={unit.devices}
                        refreshData={() => refreshData()}
                        timeZone={structure?.timezone_key || ''}
                        structure={structure}
                        unitInfo={{ unit_id: unit.id, unit_name: unit.name }}
                      />
                    )}
                    <Box position="relative" sx={{ marginTop: 2 }}>
                      <Box>
                        <Box textAlign="center" className="custom-range-picker" marginBottom="15px">
                          <RangePicker
                            format={'MMM D, YYYY'}
                            allowClear={false}
                            maxDate={dayjs(format(endOfMonth(today), 'yyyy-MM-dd'), dateFormat)}
                            onChange={(value, dateString) => handleDateChange(dateString)}
                            value={
                              range && range[0] && range[1]
                                ? [
                                    dayjs(format(range[0], 'yyyy-MM-dd'), dateFormat),
                                    dayjs(format(range[1], 'yyyy-MM-dd'), dateFormat),
                                  ]
                                : undefined
                            }
                            presets={[
                              {
                                label: <span aria-label="Current month">Current month</span>,
                                value: () => [dayjs().startOf('month'), dayjs().endOf('month')],
                              },
                            ]}
                          />
                        </Box>
                        <Typography variant="h6" align="center" color={theme.palette.grey[700]}>
                          {formatNumber(getTotalVolume(graphData || []))} gal
                        </Typography>
                      </Box>
                      <Box className={classes.chartBody}>
                        {isPermissionDenied ? (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: '400px',
                              width: '100%',
                            }}
                          >
                            <Typography variant="h6" color="text.secondary" align="center">
                              You don't have permission to view usage data.
                              <br />
                              Please contact your account owner.
                            </Typography>
                          </Box>
                        ) : (
                          <>
                            <IconButton
                              size="large"
                              className={classNames(classes.prevNextButton, classes.prevButton)}
                              onClick={handleClickPrev}
                            >
                              <ArrowLeftIcon fontSize="large" />
                            </IconButton>
                            <Box
                              sx={{
                                opacity: isLoadingUsage ? 0.2 : undefined,
                                display: 'flex',
                                flexDirection: 'column',
                              }}
                            >
                              <Chart
                                data={graphData || []}
                                x={'date'}
                                y={'gallons'}
                                createCSV={() =>
                                  Promise.resolve(
                                    generateUsageCSV(rawUsage, {
                                      start: convertTimestampToTimeZone(
                                        getUnixTime(range[0]!),
                                        timeZone ||
                                          Intl.DateTimeFormat().resolvedOptions().timeZone,
                                        false,
                                        undefined,
                                        true,
                                      ),
                                      end: convertTimestampToTimeZone(
                                        getUnixTime(range[1]!),
                                        timeZone ||
                                          Intl.DateTimeFormat().resolvedOptions().timeZone,
                                        false,
                                        undefined,
                                        true,
                                      ),
                                      timezone:
                                        timeZone ||
                                        Intl.DateTimeFormat().resolvedOptions().timeZone,
                                    }),
                                  )
                                }
                                eventType="Unit Usage Exported"
                                eventData={eventData}
                                csvTitle={`${
                                  moveByMonth
                                    ? format(startOfMonth(range[0]!), 'MM_dd_yyyy')
                                    : `${format(range[0]!, 'MM_dd_yyyy')}-${format(
                                        range[1]!,
                                        'MM_dd_yyyy',
                                      )}`
                                }_${structure?.name}_${unit.name}_usage`}
                              />
                            </Box>
                            <IconButton
                              size="large"
                              className={classNames(classes.prevNextButton, classes.nextButton)}
                              onClick={handleClickNext}
                              disabled={!canGoNext}
                            >
                              <ArrowRightIcon fontSize="large" />
                            </IconButton>
                            {isLoadingUsage && (
                              <div className={classes.chartLoader}>
                                <LinearProgress />
                              </div>
                            )}
                          </>
                        )}
                      </Box>
                    </Box>
                  </TabPanel>
                  <TabPanel value="3">
                    <AlertsView stripeConnect={true} />
                  </TabPanel>
                </TabContext>
              </div>
            </div>

            <DeleteConfirmDialog
              open={openDeleteConfirm}
              onClose={() => setOpenDeleteConfirm(false)}
              onConfirm={handleClickRemove}
              title="Do you really want to delete this unit?"
              loading={loading}
            />
          </div>
        )}

        <Dialog open={dialogModel} onClose={() => setDialogModel(false)}>
          <DialogTitle id="alert-dialog-title">
            Are you sure you want to change the model of the unit?
          </DialogTitle>
          <DialogContent></DialogContent>
          <DialogActions>
            <SWLoadingButtonOutlined onClick={() => setDialogModel(false)}>
              Cancel
            </SWLoadingButtonOutlined>
            <SWLoadingButton onClick={handleUpdateUnitFlags} autoFocus loading={loading}>
              Confirm
            </SWLoadingButton>
          </DialogActions>
        </Dialog>
      </div>
    </ErrorBoundary>
  );
};
